import { useMemo, type ReactNode } from 'react'
import { activeCategories, useCategories } from '@/store/categories'
import { usePrefs } from '@/store/prefs'
import { setFiltersOpen, useUi } from '@/store/ui'
import { Button } from './Button'
import { Sheet } from './Sheet'
import { FilterIcon, LockIcon } from './icons'
import { WhoseDropdown } from './Whose'
import './Filters.css'

// The funnel is for categories; Whose has its own button in the top bar and
// Hide done is in the Plan tab's ⋯ menu. All three are one set of filters for the
// whole app, remembered on the device until cleared. On a phone the funnel opens
// the sheet and, while a category is on, turns the accent with that category's
// dot, so a remembered category never looks like missing tasks. On a wide
// screen the category chips stay in view beside the Whose button.

/** The funnel in a top row; while a category is on, it shows that category's dot. */
export function FilterButton() {
  const categoryId = usePrefs((s) => s.filter.categoryId)
  const category = useCategories((s) => (categoryId ? s.byId[categoryId] : undefined))
  return (
    <button
      type="button"
      className={`icon-btn filt__btn ${category ? 'filt__btn--on' : ''}`}
      aria-label={category ? `Filters, ${category.name} on` : 'Filters'}
      onClick={() => setFiltersOpen(true)}
    >
      <FilterIcon size={20} />
      {category && <span className="filt__dot" style={{ background: `var(--${category.color})` }} aria-hidden="true" />}
    </button>
  )
}

/** The category chips, for the sheet and the wide row. One at a time; tapping the one that is on turns it off. */
function CategoryChips() {
  const categoryId = usePrefs((s) => s.filter.categoryId)
  const setFilter = usePrefs((s) => s.setFilter)
  // derived outside the selector: a new array from a selector re-renders without end
  const allCategories = useCategories((s) => s.all)
  const categories = useMemo(() => activeCategories(allCategories), [allCategories])
  return categories.map((c) => (
    <button key={c.id} type="button" className={`chip filt__chip ${categoryId === c.id ? 'filt__chip--on' : ''}`} aria-pressed={categoryId === c.id} onClick={() => setFilter({ categoryId: categoryId === c.id ? null : c.id })}>
      <span className="chip__dot" style={{ background: `var(--${c.color})` }} />
      {c.name}
      {c.scope === 'private' && <LockIcon size={12} title="Private" />}
    </button>
  ))
}

/** The phone's filter sheet. `extra` adds a section, such as the Calendar tab's calendar list. */
export function FilterSheet({ extra }: { extra?: ReactNode }) {
  const open = useUi((s) => s.filtersOpen)
  const categoryId = usePrefs((s) => s.filter.categoryId)
  const setFilter = usePrefs((s) => s.setFilter)
  const any = useCategories((s) => activeCategories(s.all).length > 0)
  const close = () => setFiltersOpen(false)
  return (
    <Sheet open={open} onClose={close} heading="Filters">
      <div className="filt__sheet">
        <p className="filt__helper">Applies to Plan and Calendar alike, and stays on until you clear it.</p>
        <section className="filt__sec" aria-label="Category">
          <h3 className="eyebrow">Category</h3>
          {any ? <div className="filt__chips"><CategoryChips /></div> : <p className="filt__helper">No categories yet. Add them in Settings.</p>}
        </section>
        {extra}
        <div className="filt__actions">
          {categoryId && (
            <button type="button" className="textbtn" onClick={() => setFilter({ categoryId: null })}>
              Clear
            </button>
          )}
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        </div>
      </div>
    </Sheet>
  )
}

/** On a wide screen: the Whose button, then the category chips in view, the active one in the accent. */
export function WideFilters() {
  const any = useCategories((s) => activeCategories(s.all).length > 0)
  return (
    <div className="filt__wide" role="group" aria-label="Filters">
      <WhoseDropdown />
      {any && <span className="filt__sep" aria-hidden="true" />}
      <CategoryChips />
    </div>
  )
}
